/* ============================================================================
   Doctor's Biome — desktop mega menu behaviour.

   Markup: snippets/drb-mega-menu.liquid. Paint: assets/drb-menu.css.

   ⛔ THE THREE THINGS THIS FILE EXISTS TO GET RIGHT, in order of how often they
   are got wrong:

   1. HOVER INTENT. A menu that opens the instant a pointer crosses a trigger
      fires on its way to somewhere else, and one that closes the instant the
      pointer leaves cannot be walked into diagonally. Both delays below are the
      fix, and the CLOSE delay is the one that matters — the gap between a trigger
      and its panel is 10px of nothing, and without a grace period the panel
      vanishes while the mouse is crossing it.

   2. TOUCH. This nav is display:none below 990px, but a touch laptop is a real
      thing and pointerenter fires on finger-down. Every hover path checks the
      pointer type. That is the fourth section on this site to need the guard —
      see the hero, the doctors row, and the reviews band.

   3. THE KEYBOARD. Panels are in the DOM at all times, so without management a
      Tab from the last trigger walks into thirty invisible links. The hidden
      attribute is what keeps them out of the tab order, and it is the same
      attribute the CSS keys display off, so the two can never disagree.
   ============================================================================ */
(() => {
  const OPEN_MS = 90;
  const CLOSE_MS = 220;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Is there a pointer here that can actually hover? A touch laptop matches the
     width query this nav lives behind but not this one. See the click handler. */
  const hoverMQ = window.matchMedia('(hover: hover) and (pointer: fine)');

  document.querySelectorAll('[data-drb-nav]').forEach((nav) => {
    if (nav.dataset.drbReady === 'true') return;
    nav.dataset.drbReady = 'true';

    const items = Array.from(nav.querySelectorAll('.drb-nav__item--has-panel'));
    if (!items.length) return;

    let openTimer = null;
    let closeTimer = null;
    let current = null;

    const panelOf = (item) => item.querySelector('[data-drb-nav-panel]');
    const triggerOf = (item) => item.querySelector('[data-drb-nav-trigger]');

    const clearTimers = () => {
      if (openTimer) window.clearTimeout(openTimer);
      if (closeTimer) window.clearTimeout(closeTimer);
      openTimer = null;
      closeTimer = null;
    };

    const close = (item) => {
      if (!item) return;
      const panel = panelOf(item);
      const trigger = triggerOf(item);
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (current === item) current = null;
    };

    const closeAll = (except) => {
      items.forEach((i) => {
        if (i !== except) close(i);
      });
    };

    const open = (item) => {
      const panel = panelOf(item);
      const trigger = triggerOf(item);
      if (!panel || current === item) return;

      closeAll(item);
      panel.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      current = item;

      /* ⚠️ ONE FRAME LATER, AND THIS IS NOT OPTIONAL. The panel is display:none
         when closed, so there is no starting value for the transition to run
         from — setting the class in the same task gives an instant jump. Waiting
         a frame lets the browser lay the panel out at its closed values first.
         Same trick as the hero's releasePeek(), for the same reason. */
      if (reduce.matches) {
        panel.classList.add('is-open');
      } else {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => panel.classList.add('is-open'));
        });
      }

      /* Anchor the panel under its own trigger, but never past the page gutter.
         See the positioning note in the CSS: a wide panel under the last trigger
         would otherwise run off the right edge. */
      position(item, panel);
    };

    const position = (item, panel) => {
      panel.style.left = '';
      const wrap = nav.closest('.header') || nav;
      const wrapRect = wrap.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();

      /* Where the panel would like to sit, relative to the item. */
      let left = 0;
      const wantedRight = itemRect.left + panelRect.width;
      const limit = wrapRect.right;

      if (wantedRight > limit) left = limit - wantedRight;

      /* …but never drag it left of the page gutter either. */
      if (itemRect.left + left < wrapRect.left) left = wrapRect.left - itemRect.left;

      panel.style.left = Math.round(left) + 'px';
    };

    /* ---------- pointer ---------- */
    items.forEach((item) => {
      const trigger = triggerOf(item);

      item.addEventListener('pointerenter', (event) => {
        /* ⛔ A FINGER DOES NOT HOVER. See the note at the top. */
        if (event.pointerType === 'touch') return;
        clearTimers();
        openTimer = window.setTimeout(() => open(item), OPEN_MS);
      });

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        clearTimers();
        closeTimer = window.setTimeout(() => close(item), CLOSE_MS);
      });

      /* Click toggles, so the menu is usable with a pointer that does not hover
         and by anyone who taps a trigger on a touch laptop.

         ⛔ BUT NOT ON A MOUSE. Mike, 2026-09-14: "when I click on that nav, it
         actually closes the menu. I have to hover off then back on again for it
         to pop back-up. Clicking on the main NAV link should do nothing, same
         as AG1."

         He is describing a toggle fighting the hover that already ran. The
         pointer opened the panel, the click saw current === item and closed it,
         and pointerenter cannot re-open because the pointer never left. The
         result is a menu that only reopens after a deliberate exit, which reads
         as broken rather than as a toggle.

         So on a hover pointer the trigger is inert: it opens if the panel is
         somehow shut and otherwise does nothing. The pointer owns the state,
         exactly as it does for Dawn's own disclosures in drb-nav.js.

         ⚠️ THE TWO GUARDS ARE BOTH LOAD-BEARING, and they are not the same test.
         hoverMQ excludes the touch laptop, where a tap IS the only way in and
         the toggle has to stay. event.detail excludes the KEYBOARD: Enter and
         Space on a focused button arrive here as a click with detail 0, and
         focusin has already opened the panel, so without the check Enter would
         become a no-op and there would be no way to dismiss a panel except
         Escape. A real mouse click has detail 1 or more. */
      if (trigger && trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          clearTimers();

          if (hoverMQ.matches && event.detail > 0) {
            if (current !== item) open(item);
            return;
          }

          if (current === item) close(item);
          else open(item);
        });
      }
    });

    /* ---------- keyboard and focus ----------
       focusout fires before focusin on the next element, so the check has to be
       deferred by a tick or relatedTarget lies about where focus went. */
    nav.addEventListener('focusin', (event) => {
      const item = event.target.closest('.drb-nav__item--has-panel');
      clearTimers();
      if (item) open(item);
      else closeAll(null);
    });

    nav.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!nav.contains(document.activeElement)) closeAll(null);
      }, 0);
    });

    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !current) return;
      const item = current;
      const trigger = triggerOf(item);
      close(item);
      /* Escape returns focus to the control that opened the panel. Leaving it
         inside a now-hidden panel drops the caret to the top of the document. */
      if (trigger) trigger.focus();
    });

    /* ---------- outside ---------- */
    document.addEventListener('pointerdown', (event) => {
      if (!current) return;
      if (nav.contains(event.target)) return;
      clearTimers();
      closeAll(null);
    });

    /* A resize invalidates the offset maths, and below 990px the nav is display:
       none so an open panel would be stranded. */
    window.addEventListener('resize', () => {
      if (!current) return;
      closeAll(null);
    });
  });
})();
